import React from 'react';
import { T, NUMERIC, money } from '../customer/customers.theme';
import {
  CalendarIcon,
  ChevronIcon,
  EditIcon,
  SearchIcon,
  Spinner,
  TrashIcon,
} from '../customer/CustomersUI';
import { SearchField, Thumbnail } from '../menu/MenuUI';
import { initialsOf } from '../menu/menuApi';
import { dayKeyOf, dayLabel, timeLabel } from '../../utils/appDate';
import { COPY } from './stock.copy';
import { ArrowInIcon, ArrowOutIcon, BoxIcon, MovementArrow, movementTone } from './StockIcons';

/**
 * Every movement, for every item, newest first.
 *
 * This is the half of the screen the approved design changed its mind about:
 * one item's ledger is no use to a storekeeper entering a delivery of ten
 * things. What a store actually has is documents — IN-0044, OUT-0110 — so
 * documents are what the feed lists, and an item's own history is a click away
 * on the left instead.
 *
 * A card is one document collapsed to a line: the number, why, who, how many
 * items, what it came to. Opening it shows the lines. Three shades carry the
 * meaning, the way the phone's list does: green in, brand out, red for waste.
 */

const TOOLBAR = [
  { id: 'all', label: 'All' },
  { id: 'in', label: 'In' },
  { id: 'out', label: 'Out' },
  { id: 'waste', label: 'Waste' },
];

/** Which of the four filters a document belongs to. */
function bucketOf(movement) {
  if (movement.type === 'in') return 'in';
  return movement.reason === 'Waste' ? 'waste' : 'out';
}

function Kpi({ label, value, colour }) {
  return (
    <div style={{ flex: 1, padding: '11px 16px', borderRight: `1px solid ${T.hairline}` }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.4,
          textTransform: 'uppercase',
          color: T.inkFaint,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: 17, fontWeight: 700, marginTop: 3, color: colour || T.ink, ...NUMERIC }}>
        {value}
      </div>
    </div>
  );
}

function Segment({ active, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        height: 28,
        padding: '0 12px',
        border: 'none',
        borderRadius: 6,
        background: active ? T.surface : 'transparent',
        boxShadow: active ? '0 1px 2px rgba(21,24,29,0.10)' : 'none',
        font: 'inherit',
        fontSize: 12,
        fontWeight: 600,
        color: active ? T.ink : T.inkSoft,
        cursor: 'pointer',
      }}
    >
      {label}
    </button>
  );
}

function ActionButton({ title, onClick, danger, children }) {
  return (
    <button
      type="button"
      title={title}
      onClick={(event) => {
        // The card itself opens and closes on click; its buttons must not.
        event.stopPropagation();
        onClick();
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 30,
        height: 30,
        border: `1px solid ${T.border}`,
        borderRadius: T.radiusControl,
        background: 'rgba(255,255,255,0.85)',
        color: danger ? T.danger : T.inkSoft,
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

export default function MovementFeed({
  movements,
  summary,
  stocks,
  currency,
  loading,
  query,
  onQueryChange,
  filter,
  onFilterChange,
  stations,
  stationFilter,
  onStationFilterChange,
  rangeLabel,
  onPickRange,
  rangePopover,
  rangeAnchorRef,
  onStockIn,
  onStockOut,
  onEdit,
  onCancel,
  onOpenStock,
  canWrite,
  highlightId,
}) {
  const [open, setOpen] = React.useState({});

  const imageFor = React.useCallback(
    (stockId) => {
      const found = stocks.find((item) => item._id === String(stockId));
      return found ? found.imageUrl : '';
    },
    [stocks]
  );

  const visible = React.useMemo(() => {
    const needle = query.trim().toLowerCase();

    return movements.filter((movement) => {
      if (filter !== 'all' && bucketOf(movement) !== filter) return false;

      // Only the Out tab offers it, and only Usage carries a station — so a
      // station chosen here narrows to what that kitchen or bar actually took.
      if (stationFilter) {
        const id = movement.station && typeof movement.station === 'object'
          ? movement.station._id
          : movement.station;
        if (String(id || '') !== stationFilter) return false;
      }

      if (!needle) return true;

      const haystack = [
        movement.no,
        movement.reason,
        movement.note,
        movement.stationName,
        movement.station?.name,
        movement.vendor?.name,
        ...(movement.lines || []).map((line) => line.name),
      ];

      return haystack.some((value) => String(value || '').toLowerCase().indexOf(needle) !== -1);
    });
  }, [movements, filter, query, stationFilter]);

  /** Grouped by the day they belong to, the way the phone's list reads. */
  const days = React.useMemo(() => {
    const groups = [];
    const index = {};

    visible.forEach((movement) => {
      const key = dayKeyOf(movement.date);
      if (index[key] === undefined) {
        index[key] = groups.length;
        groups.push({ key, rows: [] });
      }
      groups[index[key]].rows.push(movement);
    });

    return groups;
  }, [visible]);

  const label = (key) => {
    const today = dayKeyOf(new Date());
    if (key === today) return COPY.today;
    const yesterday = dayKeyOf(new Date(Date.now() - 86400000));
    if (key === yesterday) return COPY.yesterday;
    return dayLabel(`${key}T00:00:00.000`);
  };

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        padding: 18,
        overflow: 'hidden',
      }}
    >
      {/* the headline, and the two buttons everything starts from */}
      <div
        style={{
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: T.radiusPanel,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            padding: '14px 16px',
          }}
        >
          <span style={{ flex: 1, fontSize: 18, fontWeight: 700, color: T.ink }}>
            {COPY.movements}
          </span>

          {canWrite ? (
            <React.Fragment>
              <button
                type="button"
                onClick={onStockIn}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 36,
                  padding: '0 16px',
                  border: 'none',
                  borderRadius: T.radiusControl,
                  background: T.paid,
                  font: 'inherit',
                  fontSize: 13,
                  fontWeight: 600,
                  color: T.surface,
                  cursor: 'pointer',
                }}
              >
                <ArrowInIcon size={14} />
                {COPY.stockIn}
              </button>

              <button
                type="button"
                onClick={onStockOut}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 36,
                  padding: '0 16px',
                  border: 'none',
                  borderRadius: T.radiusControl,
                  background: T.brand,
                  font: 'inherit',
                  fontSize: 13,
                  fontWeight: 600,
                  color: T.surface,
                  cursor: 'pointer',
                }}
              >
                <ArrowOutIcon size={14} />
                {COPY.stockOut}
              </button>
            </React.Fragment>
          ) : null}
        </div>

        <div style={{ display: 'flex', borderTop: `1px solid ${T.hairline}` }}>
          <Kpi label={COPY.received} value={money(summary?.received, currency)} colour="#15803D" />
          <Kpi label={COPY.used} value={money(summary?.used, currency)} colour={T.brandInk} />
          <Kpi label={COPY.wasted} value={money(summary?.wasted, currency)} colour={T.danger} />
          <Kpi
            label={COPY.movements}
            value={`${summary?.movements || 0} · ${summary?.lines || 0} ${COPY.lines}`}
          />
        </div>
      </div>

      {/* the feed */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          background: T.surface,
          border: `1px solid ${T.border}`,
          borderRadius: T.radiusPanel,
          // NOT hidden: the date popover hangs below the toolbar and a clipped
          // calendar is a calendar nobody can use.
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            padding: '12px 14px',
            borderBottom: `1px solid ${T.hairline}`,
          }}
        >
          <div style={{ display: 'flex', gap: 3, padding: 3, background: T.shade, borderRadius: 8 }}>
            {TOOLBAR.map((entry) => (
              <Segment
                key={entry.id}
                label={entry.label}
                active={filter === entry.id}
                onClick={() => onFilterChange(entry.id)}
              />
            ))}
          </div>

          {/* Which station took it. Shown on the Out tab only: nothing else
              has a station on it, and a dead dropdown is worse than none. */}
          {filter === 'out' && stations && stations.length ? (
            <select
              value={stationFilter || ''}
              onChange={(event) => onStationFilterChange(event.target.value)}
              style={{
                height: 42,
                padding: '0 12px',
                border: `1px solid ${stationFilter ? T.brand : T.border}`,
                borderRadius: T.radiusPill,
                background: stationFilter ? T.selectedBg : T.surface,
                font: 'inherit',
                fontSize: 13,
                fontWeight: 600,
                color: stationFilter ? T.brandInk : T.inkSoft,
                cursor: 'pointer',
                outline: 'none',
              }}
            >
              <option value="">{COPY.allStations}</option>
              {stations.map((station) => (
                <option key={station._id} value={station._id}>
                  {station.name}
                </option>
              ))}
            </select>
          ) : null}

          <div style={{ flex: 1, minWidth: 220 }}>
            <SearchField
              value={query}
              onChange={onQueryChange}
              placeholder={COPY.searchMovements}
              icon={<SearchIcon size={15} />}
            />
          </div>

          <div style={{ position: 'relative' }}>
            <button
              type="button"
              ref={rangeAnchorRef}
              onClick={onPickRange}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 7,
                height: 42,
                padding: '0 14px',
                border: `1px solid ${T.border}`,
                borderRadius: T.radiusPill,
                background: T.surface,
                font: 'inherit',
                fontSize: 13,
                fontWeight: 600,
                color: T.ink,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              <CalendarIcon size={15} />
              {rangeLabel}
            </button>
            {rangePopover ? (
              <div style={{ position: 'absolute', top: '100%', right: 0, width: 318, height: 0 }}>
                {rangePopover}
              </div>
            ) : null}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: '4px 12px 12px' }}>
          {loading && !movements.length ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40, color: T.inkFaint }}>
              <Spinner size={20} />
            </div>
          ) : null}

          {!loading && !days.length ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 10,
                padding: '60px 20px',
                textAlign: 'center',
                color: T.inkSoft,
              }}
            >
              <span style={{ color: T.inkGhost }}>
                <BoxIcon size={34} />
              </span>
              <span style={{ fontSize: 14 }}>{COPY.noMovements}</span>
            </div>
          ) : null}

          {days.map((day) => (
            <div key={day.key}>
              <div
                style={{
                  padding: '12px 6px 6px',
                  fontSize: 11.5,
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  color: T.inkFaint,
                }}
              >
                {label(day.key)}
              </div>

              {day.rows.map((movement) => {
                const tone = movementTone(movement);
                const lines = movement.lines || [];
                const shown = lines.slice(0, 5);
                const isOpen = Boolean(open[movement._id]);
                const canceled = movement.status === 'canceled';

                return (
                  <div
                    key={movement._id}
                    style={{
                      marginBottom: 8,
                      borderRadius: T.radiusPanel,
                      border: `1px solid ${tone.border}`,
                      borderLeft: `4px solid ${tone.solid}`,
                      background: tone.tint,
                      overflow: 'hidden',
                      boxShadow:
                        highlightId === movement._id ? `0 0 0 2px ${T.ink}` : 'none',
                      opacity: canceled ? 0.75 : 1,
                    }}
                  >
                    <div
                      onClick={() =>
                        setOpen((current) => ({ ...current, [movement._id]: !current[movement._id] }))
                      }
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 14,
                        padding: '11px 12px',
                        cursor: 'pointer',
                      }}
                    >
                      <MovementArrow movement={movement} />

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            fontSize: 14,
                            fontWeight: 700,
                            color: T.ink,
                          }}
                        >
                          <span style={{ textDecoration: canceled ? 'line-through' : 'none' }}>
                            {movement.no}
                          </span>
                          <span
                            style={{
                              fontSize: 10.5,
                              fontWeight: 700,
                              padding: '2px 8px',
                              borderRadius: T.radiusPill,
                              background: T.surface,
                              border: `1px solid ${tone.border}`,
                              color: tone.ink,
                            }}
                          >
                            {movement.reason}
                          </span>
                          {canceled ? (
                            <span
                              style={{
                                fontSize: 10.5,
                                fontWeight: 700,
                                padding: '2px 8px',
                                borderRadius: T.radiusPill,
                                background: T.shade,
                                color: T.inkSoft,
                              }}
                            >
                              {COPY.canceled}
                            </span>
                          ) : null}
                          <span
                            style={{
                              fontSize: 13,
                              fontWeight: 500,
                              color: T.inkSoft,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {movement.vendor?.name || movement.stationName || movement.station?.name || ''}
                          </span>
                        </div>

                        <div style={{ fontSize: 12, color: T.inkSoft, marginTop: 3, ...NUMERIC }}>
                          {timeLabel(movement.date)} · {lines.length} item{lines.length === 1 ? '' : 's'}
                          {movement.note ? ` · ${movement.note}` : ''}
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                        {shown.map((line, position) => (
                          <span
                            key={`${movement._id}-${line.stock}-${position}`}
                            style={{
                              width: 32,
                              marginLeft: position === 0 ? 0 : -6,
                              border: `2px solid ${T.surface}`,
                              borderRadius: 8,
                              overflow: 'hidden',
                            }}
                          >
                            <Thumbnail
                              src={imageFor(line.stock)}
                              initials={initialsOf(line.name)}
                              height={28}
                              radius={6}
                            />
                          </span>
                        ))}
                        {lines.length > 5 ? (
                          <span style={{ marginLeft: 6, fontSize: 11.5, fontWeight: 700, color: T.inkSoft }}>
                            +{lines.length - 5}
                          </span>
                        ) : null}
                      </div>

                      <div style={{ width: 120, flexShrink: 0, textAlign: 'right' }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: tone.ink, ...NUMERIC }}>
                          {movement.type === 'in' ? '+' : '−'}
                          {money(movement.total, currency)}
                        </div>
                        <div style={{ fontSize: 11.5, color: T.inkSoft }}>{COPY.trailing(movement)}</div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                        {canWrite && !canceled ? (
                          <React.Fragment>
                            <ActionButton title={COPY.edit} onClick={() => onEdit(movement)}>
                              <EditIcon />
                            </ActionButton>
                            {/* Not a delete — see cancelMovement in stockApi. */}
                            <ActionButton title={COPY.cancelIt} danger onClick={() => onCancel(movement)}>
                              <TrashIcon />
                            </ActionButton>
                          </React.Fragment>
                        ) : null}
                        <span style={{ display: 'flex', color: T.inkFaint }}>
                          <ChevronIcon up={isOpen} />
                        </span>
                      </div>
                    </div>

                    {isOpen ? (
                      <div
                        style={{
                          padding: '6px 14px 12px 58px',
                          background: 'rgba(255,255,255,0.72)',
                          borderTop: `1px solid ${T.hairline}`,
                        }}
                      >
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                          <thead>
                            <tr>
                              {['Item', 'Qty', 'Cost', 'Total'].map((head, position) => (
                                <th
                                  key={head}
                                  style={{
                                    padding: '8px 8px 6px',
                                    textAlign: position === 0 ? 'left' : 'right',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    letterSpacing: 0.4,
                                    textTransform: 'uppercase',
                                    color: T.inkFaint,
                                  }}
                                >
                                  {head}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {lines.map((line, position) => (
                              <tr key={`${movement._id}-line-${position}`}>
                                <td style={{ padding: '7px 8px', borderTop: `1px solid ${T.hairline}` }}>
                                  <span
                                    onClick={() => onOpenStock(String(line.stock))}
                                    style={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 10,
                                      fontWeight: 600,
                                      color: T.ink,
                                      cursor: 'pointer',
                                    }}
                                  >
                                    <span style={{ width: 24, borderRadius: 6, overflow: 'hidden' }}>
                                      <Thumbnail
                                        src={imageFor(line.stock)}
                                        initials={initialsOf(line.name)}
                                        height={24}
                                        radius={6}
                                      />
                                    </span>
                                    {line.name}
                                  </span>
                                </td>
                                <td
                                  style={{
                                    padding: '7px 8px',
                                    borderTop: `1px solid ${T.hairline}`,
                                    textAlign: 'right',
                                    ...NUMERIC,
                                  }}
                                >
                                  {movement.type === 'in' ? '+' : '−'}
                                  {line.qty} {line.unit}
                                </td>
                                <td
                                  style={{
                                    padding: '7px 8px',
                                    borderTop: `1px solid ${T.hairline}`,
                                    textAlign: 'right',
                                    color: T.inkSoft,
                                    ...NUMERIC,
                                  }}
                                >
                                  {money(line.cost, currency)}
                                </td>
                                <td
                                  style={{
                                    padding: '7px 8px',
                                    borderTop: `1px solid ${T.hairline}`,
                                    textAlign: 'right',
                                    fontWeight: 600,
                                    ...NUMERIC,
                                  }}
                                >
                                  {money(line.subtotal, currency)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr>
                              <td
                                style={{
                                  padding: '8px',
                                  borderTop: `1px solid ${T.border}`,
                                  fontWeight: 700,
                                  color: T.inkSoft,
                                }}
                              >
                                {lines.length} {COPY.lines}
                              </td>
                              <td style={{ borderTop: `1px solid ${T.border}` }} />
                              <td style={{ borderTop: `1px solid ${T.border}` }} />
                              <td
                                style={{
                                  padding: '8px',
                                  borderTop: `1px solid ${T.border}`,
                                  textAlign: 'right',
                                  fontWeight: 700,
                                  ...NUMERIC,
                                }}
                              >
                                {money(movement.total, currency)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>

                        {canceled && movement.cancelReason ? (
                          <div style={{ marginTop: 8, fontSize: 12.5, color: T.danger }}>
                            {COPY.cancelReason}: {movement.cancelReason}
                          </div>
                        ) : null}

                        {movement.user?.name ? (
                          <div style={{ marginTop: 8, fontSize: 12, color: T.inkFaint }}>
                            {COPY.enteredBy} {movement.user.name}
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
